import React from "react";

export type ActionButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "success"
  | "outline"
  | "emerald"
  | "violet"
  | "teal"
  | "roseSolid"
  | "blue"
  | "ghost"
  | "link";

export type ActionButtonSize = "xs" | "sm" | "md" | "lg" | "icon";

export interface ActionButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: ActionButtonVariant;
  size?: ActionButtonSize;
  disabled?: boolean;
  loading?: boolean;
  /** Text shown while `loading` is true (default: Carregando...). */
  loadingLabel?: string;
  className?: string;
  icon?: React.ReactNode;
  type?: "button" | "submit" | "reset";
  href?: string;
  download?: string | boolean;
  target?: string;
  rel?: string;
  "aria-label"?: string;
  "aria-expanded"?: boolean;
  /** Native browser tooltip (`title` attribute). */
  tooltip?: string;
}

function variantClasses(variant: ActionButtonVariant, compact: boolean): string {
  switch (variant) {
    case "primary":
      return compact
        ? "bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white"
        : "bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white";
    case "secondary":
      return compact
        ? "bg-purple-600 hover:bg-purple-700 text-white"
        : "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white";
    case "danger":
      return compact
        ? "bg-red-600 hover:bg-red-700 text-white"
        : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white";
    case "success":
      return compact
        ? "bg-green-600 hover:bg-green-700 text-white"
        : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white";
    case "outline":
      return "bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50 dark:border-[#374151] dark:bg-[#2D3748] dark:text-slate-200 dark:hover:bg-[#374151]";
    case "emerald":
      return "bg-emerald-600 hover:bg-emerald-700 text-white";
    case "violet":
      return "bg-violet-600 hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-400 text-white";
    case "teal":
      return "bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500 text-white shadow-sm";
    case "roseSolid":
      return "bg-rose-500 hover:bg-rose-600 dark:bg-rose-600 dark:hover:bg-rose-500 text-white shadow-sm";
    case "blue":
      return "bg-blue-600 hover:bg-blue-700 text-white shadow-sm border-2 border-blue-600 dark:border-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500";
    case "ghost":
      return "bg-transparent border-0 shadow-none text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#374151]";
    case "link":
      return "bg-transparent border-0 shadow-none p-0 h-auto min-h-0 rounded-none font-medium text-sky-600 dark:text-[#63B3ED] hover:text-sky-800 dark:hover:text-blue-300 underline hover:scale-100";
    default:
      return "";
  }
}

function sizeRow(
  size: ActionButtonSize,
  isCompactVariant: boolean
): { root: string; iconWrap: string } {
  if (size === "xs") {
    return {
      root: "gap-1.5 py-1.5 px-3 text-sm rounded-lg shadow-none font-medium",
      iconWrap: "text-sm leading-none shrink-0 [&>svg]:w-4 [&>svg]:h-4",
    };
  }
  if (size === "sm") {
    return {
      root: "gap-1.5 py-2 px-4 text-sm rounded-xl shadow-none font-medium",
      iconWrap: "text-base leading-none shrink-0 [&>svg]:w-4 [&>svg]:h-4",
    };
  }
  if (size === "icon") {
    return {
      root: "p-2 rounded-lg shadow-none font-medium",
      iconWrap: "text-lg leading-none [&>svg]:w-5 [&>svg]:h-5",
    };
  }
  if (size === "md") {
    return {
      root: isCompactVariant
        ? "gap-1.5 py-2 px-4 text-sm rounded-xl shadow-none font-medium"
        : "flex items-center gap-3 py-4 px-8 text-base rounded-xl font-bold shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] disabled:transform-none",
      iconWrap: isCompactVariant
        ? "text-base leading-none shrink-0 [&>svg]:w-4 [&>svg]:h-4"
        : "text-xl [&>svg]:w-6 [&>svg]:h-6",
    };
  }
  // lg
  return {
    root: isCompactVariant
      ? "gap-1.5 py-2 px-4 text-sm rounded-xl shadow-none font-medium"
      : "flex items-center gap-3 py-6 px-10 text-lg rounded-xl font-bold shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] disabled:transform-none",
    iconWrap: isCompactVariant
      ? "text-base leading-none shrink-0 [&>svg]:w-4 [&>svg]:h-4"
      : "text-xl [&>svg]:w-6 [&>svg]:h-6",
  };
}

export function ActionButton({
  children,
  onClick,
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  loadingLabel,
  className = "",
  icon,
  type = "button",
  href,
  download,
  target,
  rel,
  "aria-label": ariaLabel,
  "aria-expanded": ariaExpanded,
  tooltip,
}: ActionButtonProps): React.ReactElement {
  const isLink = Boolean(href);
  // Gradients only for md/lg + primary-like variants; everything else uses compact row metrics.
  const useGradient =
    (size === "md" || size === "lg") &&
    ["primary", "secondary", "danger", "success"].includes(variant);
  const { root: sizeRoot, iconWrap } = sizeRow(size, !useGradient);

  const vc = variantClasses(variant, !useGradient);

  const compactBase =
    "inline-flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  const dramaticWrapper =
    "group disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none";

  let merged: string;
  if (variant === "link") {
    merged = `${compactBase} text-sm ${vc} ${className}`.trim();
  } else if (useGradient) {
    merged = `${dramaticWrapper} ${sizeRoot} ${vc} ${className}`.trim();
  } else {
    merged = `${compactBase} ${sizeRoot} ${vc} ${className}`.trim();
  }

  const showIcon = icon != null && !loading;

  const body = (
    <>
      {showIcon && <span className={iconWrap}>{icon}</span>}
      {loading ? (loadingLabel ?? "Carregando...") : children}
    </>
  );

  if (isLink && !disabled) {
    return (
      <a
        href={href}
        download={download}
        target={target}
        rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
        className={`${merged} whitespace-nowrap`.trim()}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        title={tooltip}
      >
        {body}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={merged}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      title={tooltip}
    >
      {body}
    </button>
  );
}

export default ActionButton;
